import React, { useEffect, useMemo, useState } from 'react';
import { usePwaInstallPrompt } from './PwaInstallContext';
import {
  enableWebPush,
  getBrowserTimezone,
  getWebPushReadiness,
  getWebPushSubscriptionStatus,
  updatePushPreferences,
  isPwaServiceWorkerEnabled,
} from '../../utils/webPush';
import './pwa.css';

function isMobileOrTabletViewport() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(max-width: 900px), (pointer: coarse)').matches || false;
}

export default function PwaSetupCard({ surface = 'profile' }) {
  const {
    canPrompt,
    installed,
    isAndroid,
    isIos,
    isStandalone,
    openInstalledAppMessage,
    promptInstall,
    relatedPwaInstalled,
    shouldShowInstallUi,
    shouldShowOpenAppUi,
  } = usePwaInstallPrompt();
  const [pushStatus, setPushStatus] = useState(() => getWebPushReadiness({ isIos, isStandalone }));
  const [pushSaving, setPushSaving] = useState(false);
  const [pushMessage, setPushMessage] = useState('');
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(() => isMobileOrTabletViewport());
  const installComplete = Boolean(installed || relatedPwaInstalled || isStandalone);
  const serviceWorkerAvailable = isPwaServiceWorkerEnabled();
  const offlineReady = Boolean(isStandalone && serviceWorkerAvailable);
  const dashboardBrowserInstallOnly = Boolean(
    surface === 'dashboard'
    && !isStandalone
    && (isIos || isAndroid)
    && (isIos || isAndroid || canPrompt || shouldShowInstallUi || shouldShowOpenAppUi)
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia?.('(max-width: 900px), (pointer: coarse)');
    if (!mediaQuery) return undefined;
    const update = () => setIsMobileOrTablet(mediaQuery.matches);
    update();
    mediaQuery.addEventListener?.('change', update);
    return () => mediaQuery.removeEventListener?.('change', update);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setPushStatus(getWebPushReadiness({ isIos, isStandalone }));
    getWebPushSubscriptionStatus({ isIos, isStandalone })
      .then((status) => {
        if (!cancelled) setPushStatus(status);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isIos, isStandalone]);

  const installText = useMemo(() => {
    if (isStandalone) return 'Installed';
    if (shouldShowOpenAppUi) return openInstalledAppMessage;
    if (installComplete) return 'Installed. Open Member Nest from your app screen to finish setup.';
    if (isIos && !canPrompt) return 'Open Share and choose Add to Home Screen.';
    if (isAndroid && !canPrompt) return 'Open your browser menu and choose Install app or Add to Home screen.';
    return 'Install Member Nest on this device.';
  }, [canPrompt, installComplete, isAndroid, isIos, isStandalone, openInstalledAppMessage, shouldShowOpenAppUi]);

  const handleEnablePush = async () => {
    setPushSaving(true);
    setPushMessage('');
    try {
      await enableWebPush({ isIos, isStandalone });
      await updatePushPreferences({
        enabled: true,
        timezone: getBrowserTimezone(),
      });
      const status = await getWebPushSubscriptionStatus({ isIos, isStandalone });
      setPushStatus(status);
      setPushMessage('Push notifications are enabled on this device.');
    } catch (err) {
      const status = getWebPushReadiness({ isIos, isStandalone });
      setPushStatus(status);
      setPushMessage(err.message || 'Could not enable notifications.');
    } finally {
      setPushSaving(false);
    }
  };

  const showInstallRow = (isIos || isAndroid || installComplete)
    && (!installComplete || shouldShowOpenAppUi || dashboardBrowserInstallOnly || surface === 'profile');
  const showNotificationsRow = !dashboardBrowserInstallOnly;
  const showOfflineRow = serviceWorkerAvailable && !dashboardBrowserInstallOnly
    && (isIos || isAndroid || installComplete);
  const setupComplete = installComplete && pushStatus.enabled && offlineReady;
  const dashboardRelevant = dashboardBrowserInstallOnly || (isStandalone && isMobileOrTablet);

  if (surface === 'dashboard' && (setupComplete || !dashboardRelevant)) return null;

  return (
    <div className={`pwa-setup-card pwa-setup-card--${surface}`.trim()}>
      <div className="pwa-setup-card__content">
        <h2>{surface === 'dashboard' ? 'Set up the app' : 'App setup'}</h2>
        <ul className="pwa-setup-card__checklist">
          {showInstallRow && (
            <li className={installComplete ? 'complete' : ''}>
              <span aria-hidden="true">{installComplete ? 'OK' : '1'}</span>
              <div>
                <strong>Install app</strong>
                <p>{installText}</p>
              </div>
            </li>
          )}
          {showOfflineRow && (
            <li className={offlineReady ? 'complete' : ''}>
              <span aria-hidden="true">{offlineReady ? 'OK' : '2'}</span>
              <div>
                <strong>Offline-ready</strong>
                <p>
                  {offlineReady
                    ? 'Recently loaded pages and data can be viewed when offline.'
                    : 'Open Member Nest from the installed app to use saved pages and data offline.'}
                </p>
              </div>
            </li>
          )}
          {showNotificationsRow && (
            <li className={pushStatus.enabled ? 'complete' : ''}>
              <span aria-hidden="true">{pushStatus.enabled ? 'OK' : '3'}</span>
              <div>
                <strong>Enable notifications</strong>
                <p>{pushMessage || pushStatus.message}</p>
              </div>
            </li>
          )}
        </ul>
      </div>
      <div className="pwa-setup-card__actions">
        {canPrompt && !installComplete && (
          <button type="button" className="button" onClick={promptInstall}>
            Install app
          </button>
        )}
        {pushStatus.canEnable && !pushStatus.enabled && (
          <button type="button" className="button" onClick={handleEnablePush} disabled={pushSaving}>
            {pushSaving ? 'Enabling...' : 'Enable notifications'}
          </button>
        )}
        {isStandalone && (
          <a className="button pwa-setup-card__link" href="/profile?tab=comms">
            Manage notifications
          </a>
        )}
      </div>
    </div>
  );
}
